/**
 * masks.js — mask geometry for /clone-effect.
 *
 * The clone effect composites clip B INTO clip A's own frame (same tripod, same
 * coordinate space) — so the only thing that decides where B shows is a mask.
 * Everything here answers one question: given a mask description and a canvas
 * scale, paint the ALPHA of that mask.
 *
 * Why paths and not a stored bitmap: the same mask has to render at preview
 * scale (~600 px wide) and at export scale (1080–2160 px wide) and look
 * identical. A bitmap drawn at preview scale blurs when upscaled and can no
 * longer follow a change of output size. Points survive both — the same lesson
 * the meme/collage studios learned about brush ink (see the canvas-studio
 * traps memory, §15a).
 *
 * Mask description (all geometry NORMALISED to 0..1 of the frame, so a change
 * of output resolution or aspect never moves a mask):
 *
 *   { type:  'line' | 'rect' | 'ellipse' | 'brush' | 'ai' | 'full'
 *     angle:   deg      — line only; 0 = vertical split, 90 = horizontal
 *     pos:     0..1     — line only; position along the line's normal
 *     x,y,w,h: 0..1     — rect/ellipse; centre + size
 *     radius:  0..1     — rect only; corner rounding as a share of min(w,h)
 *     strokes: [...]    — brush; [{ pts:[{x,y}], w, erase }]
 *     feather: px       — softness of the seam, in SCENE pixels
 *     invert:  bool
 *     keys:    [...]    — optional keyframes, see resolveAt() }
 *
 * `ai` carries no geometry: the alpha comes from a segmentation matte and is
 * applied by scene.js, not here.
 *
 * Exposes window.CloneMasks.
 */
(function () {
'use strict';
if (window.CloneMasks) return;

var DEG = Math.PI / 180;

/* ───────────────────────────── defaults ───────────────────────────── */

function make(type) {
    return {
        type: type || 'line',
        angle: 0,
        pos: 0.5,
        x: 0.5, y: 0.5, w: 0.5, h: 0.7,
        radius: 0,
        strokes: [],
        feather: 6,
        invert: false,
        keys: []
    };
}

function clone(m) {
    var c = JSON.parse(JSON.stringify(m || make()));
    if (!Array.isArray(c.strokes)) c.strokes = [];
    if (!Array.isArray(c.keys)) c.keys = [];
    return c;
}

/* ───────────────────────────── keyframes ─────────────────────────────
   A clone shot goes wrong the moment the performer crosses the seam. Rather
   than force one static line for the whole take, a mask may carry keyframes
   that move it over time. Only the parameters that make sense to animate are
   interpolated — the TYPE and the brush strokes are not.

   keys: [{ t: seconds, pos, angle, x, y, w, h }]  — any subset of the fields.
   Between two keys the value is interpolated with a smoothstep so the seam
   glides instead of snapping; outside the range the nearest key holds. */

var ANIMATABLE = ['pos', 'angle', 'x', 'y', 'w', 'h'];

function smoothstep(u) { return u * u * (3 - 2 * u); }

function resolveAt(mask, t) {
    var keys = mask && mask.keys;
    if (!keys || keys.length === 0) return mask;
    if (keys.length === 1) return assignKey(mask, keys[0]);

    var sorted = keys.slice().sort(function (a, b) { return a.t - b.t; });
    if (t <= sorted[0].t) return assignKey(mask, sorted[0]);
    if (t >= sorted[sorted.length - 1].t) return assignKey(mask, sorted[sorted.length - 1]);

    var i = 0;
    while (i < sorted.length - 2 && sorted[i + 1].t <= t) i++;
    var a = sorted[i], b = sorted[i + 1];
    var span = b.t - a.t;
    var u = span > 1e-6 ? smoothstep((t - a.t) / span) : 0;

    var out = {};
    for (var k in mask) if (Object.prototype.hasOwnProperty.call(mask, k)) out[k] = mask[k];
    for (var j = 0; j < ANIMATABLE.length; j++) {
        var f = ANIMATABLE[j];
        var av = (a[f] !== undefined) ? a[f] : mask[f];
        var bv = (b[f] !== undefined) ? b[f] : mask[f];
        if (typeof av === 'number' && typeof bv === 'number') out[f] = av + (bv - av) * u;
    }
    return out;
}

function assignKey(mask, key) {
    var out = {};
    for (var k in mask) if (Object.prototype.hasOwnProperty.call(mask, k)) out[k] = mask[k];
    for (var j = 0; j < ANIMATABLE.length; j++) {
        var f = ANIMATABLE[j];
        if (key[f] !== undefined) out[f] = key[f];
    }
    return out;
}

/* ───────────────────────────── line half-plane ─────────────────────────────
   The seam is the line d(p) = offset, where d projects a point onto the mask's
   normal. `pos` runs 0..1 across the WHOLE projection of the frame onto that
   normal, so pos=0 hides the clip entirely and pos=1 shows all of it, at any
   angle — which is what makes the slider feel the same for a vertical split
   and for a 30° diagonal one.

   The returned polygon deliberately overshoots the canvas by a wide margin:
   feathering is a blur, and a blur reads the transparent pixels OUTSIDE the
   shape. An edge that stops at the canvas border would fade there too, putting
   a soft grey band down the side of the frame that no one asked for. */

function halfPlanePolygon(angleDeg, pos, cw, ch) {
    var a = angleDeg * DEG;
    var nx = Math.cos(a), ny = Math.sin(a);
    var tx = -ny, ty = nx;

    // Half-extent of the canvas projected onto the normal.
    var R = (cw * Math.abs(nx) + ch * Math.abs(ny)) / 2;
    var offset = (pos * 2 - 1) * R;

    var cx = cw / 2 + nx * offset;
    var cy = ch / 2 + ny * offset;

    var BIG = (cw + ch) * 2;
    return [
        { x: cx + tx * BIG, y: cy + ty * BIG },
        { x: cx - tx * BIG, y: cy - ty * BIG },
        { x: cx - tx * BIG - nx * BIG, y: cy - ty * BIG - ny * BIG },
        { x: cx + tx * BIG - nx * BIG, y: cy + ty * BIG - ny * BIG }
    ];
}

/* Where the seam meets the canvas edges — the preview draws a handle there. */
function lineEndpoints(angleDeg, pos, cw, ch) {
    var a = angleDeg * DEG;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (cw * Math.abs(nx) + ch * Math.abs(ny)) / 2;
    var offset = (pos * 2 - 1) * R;
    var cx = cw / 2 + nx * offset, cy = ch / 2 + ny * offset;
    var tx = -ny, ty = nx;
    var L = cw + ch;
    return {
        cx: cx, cy: cy,
        x1: cx + tx * L, y1: cy + ty * L,
        x2: cx - tx * L, y2: cy - ty * L,
        nx: nx, ny: ny
    };
}

/* Signed distance from a point to the seam, in canvas px (for hit-testing). */
function distanceToLine(angleDeg, pos, cw, ch, px, py) {
    var e = lineEndpoints(angleDeg, pos, cw, ch);
    return (px - e.cx) * e.nx + (py - e.cy) * e.ny;
}

/* Turn a drag on the preview into a new `pos` (0..1) for the current angle. */
function posFromPoint(angleDeg, cw, ch, px, py) {
    var a = angleDeg * DEG;
    var nx = Math.cos(a), ny = Math.sin(a);
    var R = (cw * Math.abs(nx) + ch * Math.abs(ny)) / 2;
    if (R < 1e-6) return 0.5;
    var d = (px - cw / 2) * nx + (py - ch / 2) * ny;
    return Math.max(0, Math.min(1, (d / R + 1) / 2));
}

/* ───────────────────────────── path building ───────────────────────────── */

function roundRectPath(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, Math.min(w, h) / 2));
    if (r <= 0.5) { ctx.rect(x, y, w, h); return; }
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
}

/**
 * Paint the mask's alpha into `ctx`, opaque white where the clip should show.
 *
 * @param ctx  2D context of a canvas that is exactly cw × ch
 * @param mask mask description (already resolved through resolveAt if animated)
 * @param cw,ch canvas size in DEVICE px
 * @param S    scene→canvas scale, so `feather` (authored in scene px) blurs by
 *             the same VISUAL amount in the preview and in the export. This is
 *             the one number that cannot be folded into a ctx transform: canvas
 *             filter blur is specified in canvas pixels and ignores the CTM.
 */
function paint(ctx, mask, cw, ch, S) {
    var m = mask || make();
    var feather = Math.max(0, (m.feather || 0) * (S || 1));

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = '#fff';
    ctx.strokeStyle = '#fff';

    if (m.type === 'brush') {
        paintBrush(ctx, m, cw, ch);
        ctx.restore();
        featherInPlace(ctx, cw, ch, feather);
        return;
    }

    ctx.beginPath();

    if (m.invert) {
        // Even-odd against a frame-sized outer rect: everything EXCEPT the shape.
        // The outer rect is the canvas itself, not an overshoot — inverting is a
        // deliberate "keep the surroundings" and the surroundings end at the frame.
        ctx.rect(0, 0, cw, ch);
    }

    if (m.type === 'full') {
        if (!m.invert) ctx.rect(0, 0, cw, ch);
    } else if (m.type === 'line') {
        var poly = halfPlanePolygon(m.angle || 0, m.pos == null ? 0.5 : m.pos, cw, ch);
        ctx.moveTo(poly[0].x, poly[0].y);
        for (var i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
        ctx.closePath();
    } else if (m.type === 'rect') {
        var rw = m.w * cw, rh = m.h * ch;
        var rx = m.x * cw - rw / 2, ry = m.y * ch - rh / 2;
        roundRectPath(ctx, rx, ry, rw, rh, (m.radius || 0) * Math.min(rw, rh));
    } else if (m.type === 'ellipse') {
        ctx.ellipse(m.x * cw, m.y * ch, Math.max(1, m.w * cw / 2), Math.max(1, m.h * ch / 2), 0, 0, Math.PI * 2);
    }

    ctx.fill(m.invert ? 'evenodd' : 'nonzero');
    ctx.restore();
    featherInPlace(ctx, cw, ch, feather);
}

/* The seam is softened on the FINISHED mask, as one image — not on each fill
   and brush stroke. On Safari ≤ 17 (every iPhone browser on iOS 17 and older)
   ctx.filter works only through the page-wide shim in canvas-css-filter.js,
   and the shim can re-draw an image but not an implicit path: a filter set
   before ctx.fill() came out as a hard seam in the export. For one shape the
   two are the same pixels; for brush ink with the eraser, blurring the result
   is what "feather" means anyway (a blurred erase stroke used to leave a
   ghost ring around the erased area). */
var _featherTmp = null;
function featherInPlace(ctx, cw, ch, feather) {
    if (!(feather > 0.2)) return;
    var t = _featherTmp;
    if (!t) {
        t = _featherTmp = typeof document !== 'undefined'
            ? document.createElement('canvas') : new OffscreenCanvas(cw, ch);
    }
    if (t.width !== cw || t.height !== ch) { t.width = cw; t.height = ch; }
    var tx = t.getContext('2d');
    tx.clearRect(0, 0, cw, ch);
    tx.drawImage(ctx.canvas, 0, 0);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'copy';
    ctx.filter = 'blur(' + feather.toFixed(2) + 'px)';
    ctx.drawImage(t, 0, 0);
    ctx.restore();
}

/* Brush ink. Two rules that are easy to get wrong:
   • a single tap must leave a dot — moveTo+lineTo to the same point draws
     nothing, so a 1-point stroke is an arc;
   • the eraser is destination-out on THIS alpha canvas only. That is safe
     precisely because the mask lives on its own canvas — running it on the
     composite would rub out the video underneath instead of the mask. */
function paintBrush(ctx, m, cw, ch) {
    var strokes = m.strokes || [];
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (var s = 0; s < strokes.length; s++) {
        var st = strokes[s];
        var pts = st.pts || [];
        if (!pts.length) continue;
        var w = Math.max(1, (st.w || 0.05) * Math.min(cw, ch));
        ctx.globalCompositeOperation = st.erase ? 'destination-out' : 'source-over';
        ctx.lineWidth = w;

        if (pts.length === 1) {
            ctx.beginPath();
            ctx.arc(pts[0].x * cw, pts[0].y * ch, w / 2, 0, Math.PI * 2);
            ctx.fill();
            continue;
        }
        ctx.beginPath();
        ctx.moveTo(pts[0].x * cw, pts[0].y * ch);
        // Quadratic through segment midpoints — raw pointer samples joined by
        // straight lines read as a faceted polyline at brush widths this large.
        for (var i = 1; i < pts.length - 1; i++) {
            var mx = (pts[i].x + pts[i + 1].x) / 2 * cw;
            var my = (pts[i].y + pts[i + 1].y) / 2 * ch;
            ctx.quadraticCurveTo(pts[i].x * cw, pts[i].y * ch, mx, my);
        }
        var last = pts[pts.length - 1];
        ctx.lineTo(last.x * cw, last.y * ch);
        ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
}

/* Drop pointer samples that land on top of each other: a 240 Hz pointer packs
   hundreds of coincident points into one stroke, which bloats the project and
   slows every repaint for no visible gain. */
function pushStrokePoint(stroke, x, y, minDistNorm) {
    var pts = stroke.pts;
    if (pts.length) {
        var p = pts[pts.length - 1];
        var dx = p.x - x, dy = p.y - y;
        if (dx * dx + dy * dy < minDistNorm * minDistNorm) return false;
    }
    pts.push({ x: x, y: y });
    return true;
}

window.CloneMasks = {
    make: make,
    clone: clone,
    paint: paint,
    resolveAt: resolveAt,
    halfPlanePolygon: halfPlanePolygon,
    lineEndpoints: lineEndpoints,
    distanceToLine: distanceToLine,
    posFromPoint: posFromPoint,
    pushStrokePoint: pushStrokePoint,
    ANIMATABLE: ANIMATABLE
};
})();
